import { useState } from 'react';
import {
  Button,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Spinner,
  Box,
  Text,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from '@chakra-ui/react';
import { useHistory } from 'react-router-dom';

import PageBanner from '../../../components/pageBanner/PageBanner';
import PageContainer from '../../../components/pageContainer/PageContainer';
import { BACKEND_URL } from '../../../api/config';

function MiddleManHome() {
  const history = useHistory();

  const [scanHistory, setScanHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const navigateToVerify = () => {
    history.push('/verify');
  };

  const fetchScanHistory = async () => {
    setLoadingHistory(true);

    try {
      const res = await fetch(
        `${BACKEND_URL}/scan-statistics/user-scan-stats`,
        {
          credentials: 'include',
          cache: 'no-store',
        }
      );

      if (!res.ok) throw new Error();

      const data = await res.json();
      setScanHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch scan history', err);
      setScanHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  return (
    <PageContainer>
      <PageBanner />

      <Tabs variant="enclosed" mt={6}>
        <TabList>
          <Tab>Verify Product</Tab>
          <Tab onClick={fetchScanHistory}>Supply Chain Tracking</Tab>
        </TabList>

        <TabPanels>

          {/* ---------------- VERIFY TAB ---------------- */}
          <TabPanel>
            <Button colorScheme="blue" onClick={navigateToVerify}>
              Press Here to Verify Product
            </Button>
          </TabPanel>

          {/* ---------------- SUPPLY CHAIN TAB ---------------- */}
          <TabPanel>

            {loadingHistory ? (
              <Flex justify="center" py={10}>
                <Spinner />
              </Flex>
            ) : scanHistory.length === 0 ? (
              <Text color="gray.500">No scanned products found.</Text>
            ) : (
              (() => {
                // Sort newest first
                const sorted = [...scanHistory].sort(
                  (a, b) =>
                    new Date(b.scanTime) - new Date(a.scanTime)
                );

                // Group by certificateId
                const grouped = sorted.reduce((acc, scan) => {
                  if (!acc[scan.certificateId]) {
                    acc[scan.certificateId] = [];
                  }
                  acc[scan.certificateId].push(scan);
                  return acc;
                }, {});

                return (
                  <Accordion allowMultiple>
                    {Object.entries(grouped).map(([certId, scans]) => (
                      <AccordionItem
                        key={certId}
                        mb={4}
                        border="1px solid #e2e8f0"
                        borderRadius="lg"
                        boxShadow="sm"
                        bg="white"
                      >
                        <h2>
                          <AccordionButton _expanded={{ bg: "blue.50" }} borderRadius="lg" p={4}>
                            <Box flex="1" textAlign="left">
                              <Text fontSize="lg" fontWeight="bold" color="blue.700">
                                Certificate: {certId}
                              </Text>
                              <Text fontSize="sm" color="gray.500">
                                {scans.length} {scans.length === 1 ? 'Scan' : 'Scans'} Recorded
                              </Text>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={6} pt={4}>
                          <Box ml={4} borderLeft="2px solid" borderColor="blue.300" pl={8} position="relative">
                            {scans.map((scan, index) => (
                              <Box key={index} mb={8} position="relative">
                                {/* Timeline Dot */}
                                <Box
                                  position="absolute"
                                  left="-41px"
                                  top="4px"
                                  width="16px"
                                  height="16px"
                                  bg="blue.500"
                                  border="3px solid white"
                                  borderRadius="50%"
                                  boxShadow="0 0 0 2px var(--chakra-colors-blue-200)"
                                />

                                {/* Content Card */}
                                <Box
                                  bg="gray.50"
                                  p={4}
                                  borderRadius="md"
                                  border="1px solid"
                                  borderColor="gray.100"
                                  _hover={{ boxShadow: "md", borderColor: "blue.200" }}
                                  transition="all 0.2s"
                                >
                                  <Flex justify="space-between" align="center" mb={2} gap={4}>
                                    <Text fontWeight="bold" fontSize="md" color="gray.800">
                                      {scan.location || 'Unknown Location'}
                                    </Text>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.500" bg="gray.200" px={2} py={1} borderRadius="full">
                                      {new Date(scan.scanTime).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                    </Text>
                                  </Flex>

                                  <Flex gap={4} mt={3}>
                                    <Box flex="1">
                                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                                        Account Type
                                      </Text>
                                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                                        {scan.accountType}
                                      </Text>
                                    </Box>
                                    <Box flex="1">
                                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                                        Action
                                      </Text>
                                      <Text fontSize="sm" fontWeight="semibold" color="blue.600">
                                        {scan.scanType}
                                      </Text>
                                    </Box>
                                  </Flex>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                );
              })()
            )}

          </TabPanel>

        </TabPanels>
      </Tabs>
    </PageContainer>
  );
}

export default MiddleManHome;